import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useDailyVitals } from "@/hooks/use-daily-vitals";
import { SkeletonLoader, EmptyState } from "@/components/system";

/**
 * 30-day HRV trend with a 7-day rolling baseline overlay so it's easy to
 * tell when the night-to-night reading is dropping below the baseline.
 */

export function HRVTrendChart() {
  const q = useDailyVitals(30);

  if (q.isLoading) return <SkeletonLoader variant="card" height={240} />;
  const rows = (q.data ?? []).slice().reverse();
  const hrvOnly = rows.filter((r) => r.hrv_status_ms != null);
  if (hrvOnly.length === 0) {
    return (
      <article style={cardStyle}>
        <EmptyState headline="No HRV data yet." sub="Connect Garmin or Apple Health → HAE to populate." />
      </article>
    );
  }

  const data = rows.map((r, i) => {
    const window = rows.slice(Math.max(0, i - 6), i + 1).filter((x) => x.hrv_status_ms != null);
    const baseline = window.length ? window.reduce((s, x) => s + Number(x.hrv_status_ms), 0) / window.length : null;
    return {
      date: r.date,
      hrv: r.hrv_status_ms,
      baseline7: baseline,
    };
  });

  return (
    <article style={cardStyle}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: "var(--type-body)", fontWeight: 500 }}>HRV · 30 days</h3>
        <span className="mono" style={{ fontSize: 12, color: "var(--fg-3)" }}>{hrvOnly.length} days</span>
      </header>
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--hair)" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="date"
              stroke="var(--fg-4)"
              fontSize={10}
              tickFormatter={(d) => new Date(d as string).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
              minTickGap={36}
            />
            <YAxis stroke="var(--fg-4)" fontSize={10} width={36} tickLine={false} axisLine={{ stroke: "var(--hair)" }} />
            <Tooltip contentStyle={{ background: "var(--bg-3)", border: "1px solid var(--hair-2)", borderRadius: "var(--radius-card)", fontSize: 12 }} />
            <Line type="monotone" dataKey="hrv" name="HRV (ms)" stroke="var(--accent)" strokeWidth={1.5} dot={{ r: 2 }} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="baseline7" name="7-day avg" stroke="var(--fg-3)" strokeWidth={1.2} strokeDasharray="3 3" dot={false} isAnimationActive={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--bg-2)",
  border: "1px solid var(--hair)",
  borderRadius: "var(--radius-card)",
  padding: "var(--space-5)",
};
